import type { ActionBadgeColor } from "@/components/ui/action-badge"
import type { CmsCategory } from "@/lib/cms"

/** Inline style for a category accent: text in the CMS colour, soft tinted
 *  background derived from it. Neutral grey when the category has no colour
 *  set (pre-seed / editor left it blank). */
export function categoryAccentStyle(color?: string | null): {
  color: string
  backgroundColor: string
} {
  if (!color) return { color: "#404040", backgroundColor: "#f5f5f5" }
  return { color, backgroundColor: `color-mix(in oklab, ${color} 12%, white)` }
}

/** Group items by their `category` slug, returning only CMS categories that
 *  have >=1 item, ordered by the CMS `order` (name as tiebreak). This replaces
 *  the old hardcoded BIOMARKER_CATEGORIES/DISEASE_CATEGORIES iteration — the
 *  CMS collection now drives which sections render and in what order. */
export function groupItemsByCmsCategory<
  T extends { category: string },
  C extends Pick<CmsCategory, "slug" | "name" | "order"> = CmsCategory,
>(items: T[], categories: C[]): { category: C; items: T[] }[] {
  const byCat = new Map<string, T[]>()
  for (const it of items) {
    const arr = byCat.get(it.category)
    if (arr) arr.push(it)
    else byCat.set(it.category, [it])
  }
  return categories
    .filter((c) => byCat.has(c.slug))
    .slice()
    .sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name)
    )
    .map((c) => ({ category: c, items: byCat.get(c.slug)! }))
}

export const PACKAGE_LABELS: Record<string, string> = {
  core: "Core / Full-body MRI",
  advanced: "Advanced",
  pro: "Pro",
  blood: "Advanced blood test",
  "prevention-starter": "Prevention Starter",
  "blood-followup": "Blood follow-up",
  "addon-single": "Add-on",
  "addon-calcium": "Calcium score",
  "addon-body": "Body composition",
  "addon-brain": "Brain analysis",
  "addon-mamma": "Mamma MRI",
  "addon-bvitamins": "B-vitamins",
  "addon-hormones-f": "Hormones (women)",
  "addon-hormones-m": "Hormones (men)",
  "addon-omega": "Omega-3 & 6",
  "addon-trace-elements": "Trace elements",
  "addon-heavy-metals": "Heavy metals",
  "addon-coeliac": "Coeliac screen",
  "addon-autoimmune": "Autoimmune panel",
  "addon-dexa": "DEXA scan",
  "addon-gender-health": "Gender health",
}

/**
 * CMS package tags the site shows in another tag's slot.
 *
 * Switzerland sells "Essential B-vitamins & homocysteine" where DE, NL and AT
 * sell the B12 + folate panel (AHD-12960). The CMS tags the Swiss product
 * `addon-bvitamins-homocysteine` so Ella can list its markers, but on the site
 * the two are one add-on: same test page, same price key (`addonBVitamins` in
 * `lib/pricing.ts`). B12 and folate carry both tags; homocysteine carries only
 * the Swiss one and is hidden outside CH (`hideInCountries`), so folding the
 * tag gives every market its own panel with no per-country branch here.
 */
const PACKAGE_TAG_SLOT: Record<string, string> = {
  "addon-bvitamins-homocysteine": "addon-bvitamins",
}

/** A biomarker's CMS package tags as the site's package ids, deduplicated. */
export function sitePackageIds(packages: string[]): string[] {
  return Array.from(new Set(packages.map((id) => PACKAGE_TAG_SLOT[id] ?? id)))
}

/**
 * `sitePackageIds` over a CMS biomarker list. A draft the editor has not
 * filled in yet arrives with `packages: null` (the preview fetchers pass those
 * through untouched), so only a real tag list is rewritten.
 */
export function withSitePackageIds<T extends { packages: string[] }>(
  docs: T[]
): T[] {
  return docs.map((doc) =>
    Array.isArray(doc.packages)
      ? { ...doc, packages: sitePackageIds(doc.packages) }
      : doc
  )
}

/** Map package filter ID → test page slug for "learn more" links */
export const PACKAGE_TO_TEST_SLUG: Record<string, string> = {
  core: "full-body-mri",
  advanced: "full-body-mri",
  pro: "full-body-mri",
  blood: "advanced-blood-test",
  "prevention-starter": "prevention-starter",
  "addon-calcium": "calcium-score",
  "addon-body": "body-composition",
  "addon-brain": "brain-volumetry",
  "addon-mamma": "mamma-mri",
  "addon-dexa": "dexa-scan",
  "addon-gender-health": "gender-health",
  "addon-bvitamins": "essential-b-vitamins-test",
  "addon-hormones-f": "hormone-blood-test-women",
  "addon-hormones-m": "hormone-blood-test-men",
  "addon-omega": "omega-blood-test",
  "addon-trace-elements": "trace-elements-blood-test",
  "addon-heavy-metals": "heavy-metals-blood-test",
  "addon-coeliac": "coeliac-blood-test",
}

/**
 * The single-marker add-ons' own test pages, keyed by the marker's Payload doc
 * id (locale-invariant — the same ids the three pages pass as `addonIds`).
 * Lp(a), cortisol and PSA share one tag, `addon-single`, so the tag alone
 * cannot say which page a marker's "included in" row should open.
 */
const SINGLE_MARKER_TEST_SLUG: Record<string, string> = {
  "188": "lpa",
  "190": "cortisol-blood-test",
  "47": "psa-blood-test",
}

/** Test page slug of a single-marker add-on, by the marker's Payload doc id. */
export function singleMarkerTestSlug(
  biomarkerDocId: string | number
): string | undefined {
  return SINGLE_MARKER_TEST_SLUG[String(biomarkerDocId)]
}

/**
 * Map test page slug → biomarker package ID, for tests whose detail page
 * displays a "biomarkers tested" count derived from the CMS biomarkers
 * collection (see `tests/[slug]/page.tsx`). Limited to blood-based tests
 * where the count is meaningful — MRI tests show body regions, not
 * biomarkers, and aren't included.
 */
export const TEST_SLUG_TO_BIOMARKER_PACKAGE: Record<string, string> = {
  "advanced-blood-test": "blood",
  "prevention-starter": "prevention-starter",
  "essential-b-vitamins-test": "addon-bvitamins",
  "hormone-blood-test-women": "addon-hormones-f",
  "hormone-blood-test-men": "addon-hormones-m",
  "omega-blood-test": "addon-omega",
  "trace-elements-blood-test": "addon-trace-elements",
  "heavy-metals-blood-test": "addon-heavy-metals",
  "coeliac-blood-test": "addon-coeliac",
}

/**
 * Ordered package taxonomy. Controls display order of the filter chips / rail
 * rows and of the "included in" row tags; only packages present in the data are
 * shown. The translation key maps to the `whatWeDetect` namespace.
 *
 * Entries listed in `TAG_ONLY_PACKAGE_IDS` keep their place in this order (so
 * their row tag still sorts and localizes) but are never offered as a filter.
 */
export const PACKAGE_FILTER_ORDER: { id: string; translationKey: string }[] = [
  { id: "core", translationKey: "filterCoreMri" },
  { id: "advanced", translationKey: "filterAdvanced" },
  { id: "pro", translationKey: "filterPro" },
  { id: "blood", translationKey: "filterBlood" },
  { id: "prevention-starter", translationKey: "filterPreventionStarter" },
  { id: "blood-followup", translationKey: "filterBloodFollowup" },
  { id: "addon-single", translationKey: "filterAddonSingle" },
  { id: "addon-calcium", translationKey: "filterAddonCalcium" },
  { id: "addon-body", translationKey: "filterAddonBody" },
  { id: "addon-brain", translationKey: "filterAddonBrain" },
  { id: "addon-mamma", translationKey: "filterAddonMamma" },
  { id: "addon-bvitamins", translationKey: "filterAddonBVitamins" },
  { id: "addon-hormones-f", translationKey: "filterAddonHormonesF" },
  { id: "addon-hormones-m", translationKey: "filterAddonHormonesM" },
  { id: "addon-omega", translationKey: "filterAddonOmega" },
  { id: "addon-trace-elements", translationKey: "filterAddonTraceElements" },
  { id: "addon-heavy-metals", translationKey: "filterAddonHeavyMetals" },
  { id: "addon-coeliac", translationKey: "filterAddonCoeliac" },
  { id: "addon-autoimmune", translationKey: "filterAddonAutoimmune" },
  { id: "addon-dexa", translationKey: "filterAddonDexa" },
  { id: "addon-gender-health", translationKey: "filterAddonGenderHealth" },
]

/**
 * Packages that only ever surface as an "included in" tag on a row — never as a
 * filter chip, rail row, or `/biomarkers/{id}` listing route.
 *
 * `addon-single` marks the one-off blood add-ons (Lp(a), PSA, cortisol). Worth
 * labelling on the row so a visitor sees the marker isn't in a panel, but not
 * worth a filter of its own: the explorer's add-on list stays flat rather than
 * gaining a group that holds three markers.
 */
export const TAG_ONLY_PACKAGE_IDS = new Set<string>(["addon-single"])

export interface PackageFilter {
  id: string
  label: string
  /** Imaging packages in a checkup market (DE): not bookable yet, so the chip
   *  carries a "Bald verfügbar" pill and moves into the "Weitere" overflow. */
  comingSoon?: boolean
}

function availablePackages(items: { packages: string[] }[]): Set<string> {
  const available = new Set<string>()
  for (const item of items) {
    for (const p of item.packages) available.add(p)
  }
  return available
}

/**
 * Build the package filter chip list dynamically from item data.
 * Only packages that exist in at least one item are included; tag-only
 * packages are excluded (see `TAG_ONLY_PACKAGE_IDS`).
 */
export function buildPackageFilters(
  items: { packages: string[] }[],
  t: (key: string) => string
): PackageFilter[] {
  const available = availablePackages(items)
  return PACKAGE_FILTER_ORDER.filter(
    (pkg) => available.has(pkg.id) && !TAG_ONLY_PACKAGE_IDS.has(pkg.id)
  ).map((pkg) => ({ id: pkg.id, label: t(pkg.translationKey) }))
}

/**
 * The tag-only packages present in the data, with their translated labels. The
 * explorers merge these into their id → label map so a tag-only package still
 * renders a localized "included in" tag on the row, without ever appearing in
 * the filter rail alongside the real packages.
 */
export function buildTagOnlyPackages(
  items: { packages: string[] }[],
  t: (key: string) => string
): PackageFilter[] {
  const available = availablePackages(items)
  return PACKAGE_FILTER_ORDER.filter(
    (pkg) => available.has(pkg.id) && TAG_ONLY_PACKAGE_IDS.has(pkg.id)
  ).map((pkg) => ({ id: pkg.id, label: t(pkg.translationKey) }))
}

// ── Package "included in" chips — the shared vocabulary for the biomarkers
//    listing rows AND the chat marker card, so the two never drift ───────────

/** Brand tint per package for the "included in" chips — Core green, Advanced
 *  blue, Pro aubergine, Blood pink (echoing the pricing-page MRI colours);
 *  add-ons and anything unmapped stay neutral. */
export const PACKAGE_BADGE_COLOR: Record<string, ActionBadgeColor> = {
  core: "light-green",
  advanced: "light-blue",
  pro: "aubergine",
  blood: "light-pink",
  "prevention-starter": "light-pink",
}

/** DS badge colour for a package id (neutral for add-ons / unknown). */
export function packageBadgeColor(id: string): ActionBadgeColor {
  return PACKAGE_BADGE_COLOR[id] ?? "neutral"
}

/** Short chip label (English). The tiers get a one-word form — the full
 *  `PACKAGE_LABELS` names ("Advanced blood test") don't fit a chip — while
 *  add-ons reuse their full label. Used as the fallback when no localized
 *  string is available; localized callers go through `packageChipLabelKey`. */
const PACKAGE_CHIP_SHORT_EN: Record<string, string> = {
  core: "Core",
  advanced: "Advanced",
  pro: "Pro",
  blood: "Blood",
  "blood-followup": "Follow-up",
}

/** English chip label for a package id (short for tiers, full for add-ons). */
export function packageChipLabel(id: string): string {
  return PACKAGE_CHIP_SHORT_EN[id] ?? PACKAGE_LABELS[id] ?? id
}

/** Short-label translation keys for the panel/tier chips, keyed by
 *  `whatWeDetect` key (not literal text): hardcoding English left "Blood" /
 *  "Follow-up" untranslated on every non-EN locale while the unlisted packages
 *  localized normally. Shared by the biomarkers listing and the chat card. */
export const SHORT_PACKAGE_LABEL_KEY: Record<string, string> = {
  core: "shortCore",
  advanced: "shortAdvanced",
  pro: "shortPro",
  blood: "shortBlood",
  "blood-followup": "shortBloodFollowup",
}

/** `whatWeDetect` translation key for a package's chip label — the short tier
 *  key (`shortCore`…) for a panel/tier, else the package's filter key. Mirrors
 *  the biomarkers listing's `chipLabel`, so the card's chips read identically.
 *  Undefined for an unknown id (caller falls back to `packageChipLabel`). */
export function packageChipLabelKey(id: string): string | undefined {
  return (
    SHORT_PACKAGE_LABEL_KEY[id] ??
    PACKAGE_FILTER_ORDER.find((p) => p.id === id)?.translationKey
  )
}

const PACKAGE_ORDER_INDEX = new Map(
  PACKAGE_FILTER_ORDER.map((p, i) => [p.id, i])
)

/** A package id we render as a chip (i.e. one in the canonical order). Unknown
 *  CMS tags are dropped rather than shown raw. */
export function isCanonicalPackage(id: string): boolean {
  return PACKAGE_ORDER_INDEX.has(id)
}

/** Sort package ids by the canonical order (Core → Advanced → Pro → Blood →
 *  Follow-up → add-ons), so chips read the same on every row regardless of the
 *  biomarker's arbitrary CMS `packages` order. */
export function orderPackages(ids: string[]): string[] {
  return [...ids].sort(
    (a, b) =>
      (PACKAGE_ORDER_INDEX.get(a) ?? Infinity) -
      (PACKAGE_ORDER_INDEX.get(b) ?? Infinity)
  )
}

/** Imaging (MRI / CT / DEXA) package filter ids. In a `checkup` market (DE)
 *  these aren't bookable yet — they get a "Bald verfügbar" pill and move into
 *  the "Weitere" overflow so the bookable blood tests lead. */
export const IMAGING_PACKAGE_IDS = new Set<string>([
  "core",
  "advanced",
  "pro",
  "addon-calcium",
  "addon-body",
  "addon-brain",
  "addon-mamma",
  "addon-dexa",
  "addon-gender-health",
])

/** Package filter chips dropped from the DE overview entirely (not just
 *  reordered) — trace elements, heavy metals and coeliac (CH-only add-ons) aren't
 *  surfaced as a top-level filter on /de/de. Filtering is order-independent,
 *  so dropping the chip only hides the filter, never the underlying
 *  biomarkers (still reachable under "Alle"). */
const DE_HIDDEN_FILTER_IDS = new Set<string>([
  "addon-trace-elements",
  "addon-heavy-metals",
  "addon-coeliac",
  "blood",
])

/** Package filter chips shown ONLY on the DE market — `prevention-starter`
 *  replaces `blood` as the bookable blood-check-up product in Germany.
 *  Dropped outright (not just reordered) on every other market. */
const DE_ONLY_FILTER_IDS = new Set<string>(["prevention-starter"])

/** Package filter chips dropped on AT — Austria sells the MRI Advanced and Pro
 *  packages only (every standalone test is `at-de: false` in `data/tests.ts`),
 *  so the Core tier, the blood tests, trace elements, heavy metals, coeliac and
 *  the CT / DEXA / Mamma MRI / brain-analysis / body-composition add-ons have
 *  nothing to book. */
const AT_HIDDEN_FILTER_IDS = new Set<string>([
  // No Core tier in Austria (AHD-13988); its markers stay under Advanced/Pro.
  "core",
  "blood",
  "blood-followup",
  "addon-trace-elements",
  "addon-heavy-metals",
  "addon-coeliac",
  "addon-calcium",
  "addon-dexa",
  "addon-mamma",
  // Brain analysis is part of Pro on AT, not a standalone add-on (AHD-13691).
  "addon-brain",
  // Body composition is part of Advanced and Pro on AT, not sold on its own
  // (AHD-14016).
  "addon-body",
])

/**
 * DE: lead the package filter chips with the bookable blood tests and push the
 * coming-soon imaging packages into the "Weitere" overflow (flagged
 * `comingSoon` for the "Bald verfügbar" pill). Trace elements are dropped
 * outright. Order is purely presentational — filtering is
 * `item.packages.includes(id)`, so this NEVER changes which health risks /
 * biomarkers a package maps to. AT drops the blood packages it doesn't sell.
 * Every other market is returned as-is.
 */
export function orderFiltersForCountry(
  filters: PackageFilter[],
  country: string
): PackageFilter[] {
  if (country === "at")
    return filters.filter(
      (f) => !DE_ONLY_FILTER_IDS.has(f.id) && !AT_HIDDEN_FILTER_IDS.has(f.id)
    )
  if (country !== "de")
    return filters.filter((f) => !DE_ONLY_FILTER_IDS.has(f.id))
  const shown = filters.filter((f) => !DE_HIDDEN_FILTER_IDS.has(f.id))
  const blood = shown.filter((f) => !IMAGING_PACKAGE_IDS.has(f.id))
  const imaging = shown
    .filter((f) => IMAGING_PACKAGE_IDS.has(f.id))
    .map((f) => ({ ...f, comingSoon: true }))
  return [...blood, ...imaging]
}

/**
 * DE (health-risks explorer): drop the imaging packages outright rather than
 * demoting them. `orderFiltersForCountry` keeps them as "coming soon" for a
 * market that will get the scan later; /de/de sells the blood check-up and its
 * blood add-ons only, so nothing on that page may point at an MRI, CT or DEXA
 * tier. Compose it AFTER `orderFiltersForCountry` so the DE-specific hidden
 * filters (trace elements) still apply. Every other market is returned as-is.
 */
export function dropImagingFiltersForCountry(
  filters: PackageFilter[],
  country: string
): PackageFilter[] {
  if (country !== "de") return filters
  return filters.filter((f) => !IMAGING_PACKAGE_IDS.has(f.id))
}

/**
 * Health risks the German blood check-up is what finds: detected by blood, and
 * tagged to at least one package Germany actually sells (an imaging-only
 * package tag means the finding rides on the MRI tiers' extra blood work, not
 * on the 67-marker panel). Used to scope the /de/de health-risks explorer;
 * every other market lists the full catalog.
 */
export function isBloodOnlyMarketItem(item: {
  detectionMethod: string
  packages: string[]
}): boolean {
  return (
    item.detectionMethod === "blood" &&
    item.packages.some((p) => !IMAGING_PACKAGE_IDS.has(p))
  )
}

/**
 * Biomarkers the German check-up actually reports back. A biomarker carries no
 * `detectionMethod`, so its package tags are the only signal: an imaging-only
 * tag set means the value is read off the scan itself (the brain volumes, the
 * abdominal-fat measures, the liver PDFF, the calcium score, SCORE2), and no
 * blood draw in Germany produces it. One non-imaging tag is enough to keep the
 * row, because the MRI tiers bundle the blood work and so sit on almost every
 * blood marker alongside `blood`. `blood` itself (the advanced blood test)
 * doesn't count either: Germany sells `prevention-starter` in its place, so a
 * marker only the advanced panel measures isn't on a German report. Used to
 * scope the /de/de biomarker glossary; every other market lists the full
 * catalogue.
 */
export function isBloodOnlyMarketBiomarker(item: {
  packages: string[]
}): boolean {
  return item.packages.some(
    (p) => !IMAGING_PACKAGE_IDS.has(p) && !DE_NOT_SOLD_BLOOD_IDS.has(p)
  )
}

/** Non-imaging package ids Germany doesn't sell — `blood` (advanced blood
 *  test) is replaced by `prevention-starter` there. */
const DE_NOT_SOLD_BLOOD_IDS = new Set<string>(["blood"])

/** @deprecated Use PACKAGE_FILTER_ORDER + buildPackageFilters() instead */
export const FILTER_PACKAGES = [
  { id: "core", label: "Core / Full-body MRI" },
  { id: "advanced", label: "Advanced" },
  { id: "pro", label: "Pro" },
  { id: "blood", label: "Advanced blood test" },
]

/** All package IDs that can appear as /biomarkers/{id} or /what-we-detect/{id}
 *  filter paths. Tag-only packages are excluded — with no chip to reflect the
 *  active filter, the route would render a listing nothing on the page explains. */
export const FILTER_PACKAGE_IDS = new Set(
  Object.keys(PACKAGE_LABELS).filter((id) => !TAG_ONLY_PACKAGE_IDS.has(id))
)

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/\([^)]*\)/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-+/g, "-")
}

// ─── CMS-managed categories (the `categories` collection) ────────────────────
//
// The CMS is the SOLE source of truth for category naming + description (no
// hardcoded fallback — mirrors the faq-categories cutover). The CMS slug equals
// the biomarker/disease `category` enum value (1:1), so consumers look up a
// category's localized name/description by that value directly. The hardcoded
// `CATEGORIES` arrays remain only for the non-localized structural bits the CMS
// doesn't own: icon, colour, grouping membership, and display order.

/**
 * Index a CMS categories list by slug (== the granular `category` enum value).
 */
export function indexCmsCategories(
  cats: CmsCategory[]
): Map<string, CmsCategory> {
  const bySlug = new Map<string, CmsCategory>()
  for (const c of cats) bySlug.set(c.slug, c)
  return bySlug
}

/**
 * Resolve a granular category id to its CMS-managed localized name +
 * description. Throws when the id has no CMS doc — every biomarker/disease
 * `category` enum value must be seeded in the `categories` collection. We fail
 * loud rather than mask a seed gap with a hardcoded label (per the no-fallback
 * rule); the seeder lives at marketing-site-cms `scripts/seed-granular-categories.ts`.
 */
export function cmsCategoryText(
  index: Map<string, CmsCategory>,
  id: string
): { name: string; description: string } {
  const c = index.get(id)
  if (!c) {
    throw new Error(
      `No CMS category for id "${id}" — seed it in the marketing-site-cms \`categories\` collection.`
    )
  }
  return { name: c.name, description: c.description ?? "" }
}
